import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const headers = () => ({ token: localStorage.getItem('token') });

export default function HelpAssistant({ onSourceNavigate } = {}) {
  const [status, setStatus] = useState(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        const response = await axios.get(`${API_URL}/help-assistant/status`, { headers: headers(), signal: controller.signal });
        if (!controller.signal.aborted) setStatus(response.data);
      } catch {
        if (!controller.signal.aborted) setStatus({ enabled: false });
      }
    }
    load();
    return () => { controller.abort(); request.current?.abort(); };
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (busy || !status?.enabled || question.trim().length < 3) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setAnswer(null); setError('');
    try {
      const response = await axios.post(`${API_URL}/help-assistant/ask`, { question: question.trim() },
        { headers: headers(), signal: controller.signal, timeout: 20000 });
      if (!controller.signal.aborted) setAnswer(response.data);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.response?.data?.message || 'Frågan kunde inte besvaras. Försök igen senare.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  return <section aria-label="Fråga om appen">
    {(!status?.enabled || status.mode !== 'bedrock') && <p className="mt-2 text-sm text-slate-600" role="status">
      {!status ? 'Kontrollerar tillgänglighet…' : !status.enabled ? 'Assistenten är inte tillgänglig just nu. Du kan öppna hjälpen via länken nedan.' :
          'Dokumentsökning är aktiv. AI är inte inkopplad i detta läge.'}
    </p>}
    <a className="mt-2 inline-block text-sm font-semibold text-sky-700 underline" href="/hjalp" onClick={onSourceNavigate}>Öppna hjälpen</a>
    {status?.enabled && <form onSubmit={submit} className="mt-4 space-y-3">
      <label className="block text-sm font-semibold text-slate-800" htmlFor="help-question">Vad vill du veta om appen?</label>
      <textarea id="help-question" className="theme-input w-full" rows={3} maxLength={500} minLength={3} required
        value={question} onChange={event => setQuestion(event.target.value)} disabled={busy}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) submit(event);
        }}
        placeholder="Till exempel: Visa övningar med handstående" aria-describedby="help-question-info" />
      <p id="help-question-info" className="text-xs text-slate-600">Skriv en fråga i taget, högst 500 tecken. Enter skickar frågan, Shift+Enter ger en ny rad.</p>
      <button className="theme-button-primary px-5 py-3 disabled:opacity-50" type="submit" disabled={busy || question.trim().length < 3}>
        {busy ? 'Söker svar…' : 'Sök svar'}
      </button>
    </form>}
    {error && <p role="alert" className="theme-alert-error mt-4">{error}</p>}
    {answer && <div className="mt-4" aria-live="polite">
      <p className="font-semibold text-slate-800">{answer.message}</p>
      {answer.videos?.length > 0 && <article className="theme-panel mt-3" aria-label="Övningsträffar">
        <ul className="space-y-4">
          {answer.videos.map(video => <li key={video.id}>
            <h3 className="font-semibold text-slate-800">{video.title}</h3>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{video.description}</p>
            <a className="mt-2 inline-block font-semibold text-sky-700 underline" href={video.href} onClick={onSourceNavigate}>Öppna övningen</a>
          </li>)}
        </ul>
      </article>}
      {answer.sources.map(source => <article key={source.id} className="theme-panel mt-3">
        <h3 className="font-semibold text-slate-800">{source.title}</h3>
        <blockquote className="mt-2 whitespace-pre-line text-sm text-slate-600">{source.excerpt}</blockquote>
        <a className="mt-3 inline-block font-semibold text-sky-700 underline" href={source.href} onClick={onSourceNavigate}>Läs avsnittet i hjälpen</a>
      </article>)}
    </div>}
  </section>;
}
